import { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { api, relTime } from '../lib/api'
import { useOperatorAlerts, useTitleCount } from './OperatorAlerts'

/** Topbar bell with a dropdown of recent account activity (audit events). */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)

  const { data } = useQuery({
    queryKey: ['activity', 'bell'],
    queryFn: () => api.activity.list(8, 0),
    staleTime: 30_000,
  })
  const rows = data?.data ?? []
  const { pending, count } = useOperatorAlerts()
  useTitleCount(count)

  useEffect(() => {
    function onDoc(e: MouseEvent) { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const label = (a: string) => a.replace(/[._]/g, ' ').replace(/^\w/, c => c.toUpperCase())

  return (
    <div className="bell-wrap" ref={wrap}>
      <button className="topbar-bell" aria-label="Notifications" onClick={() => setOpen(o => !o)}>
        <Bell size={18} />
        {count > 0
          ? <span className="bell-count">{count > 9 ? '9+' : count}</span>
          : rows.length > 0 && <span className="bell-dot" />}
      </button>

      {open && (
        <div className="bell-menu">
          <div className="bell-head">
            <strong>Activity</strong>
            <NavLink to="/activity" className="small" onClick={() => setOpen(false)}>View all →</NavLink>
          </div>
          {count > 0 && (
            <div className="bell-action">
              <div className="bell-action-head">Action needed</div>
              {pending.slice(0, 5).map(p => (
                <NavLink key={p.tenant_id} to={`/admin/verifications/${p.tenant_id}`} className="bell-item" onClick={() => setOpen(false)}>
                  <span className="bell-dot-item urgent" />
                  <div className="bell-item-body">
                    <span className="bell-item-title">Verification request — {p.name}</span>
                    <span className="bell-item-time">{p.account_type} · {p.documents} document{p.documents === 1 ? '' : 's'}{p.submitted_at ? ` · ${relTime(p.submitted_at)}` : ''}</span>
                  </div>
                </NavLink>
              ))}
              {count > 5 && <NavLink to="/admin/verifications" className="bell-more" onClick={() => setOpen(false)}>+{count - 5} more →</NavLink>}
            </div>
          )}
          <div className="bell-list">
            {rows.length === 0
              ? <div className="bell-empty">You’re all caught up.</div>
              : rows.map((r, i) => (
                <NavLink key={i} to="/activity" className="bell-item" onClick={() => setOpen(false)}>
                  <span className="bell-dot-item" />
                  <div className="bell-item-body">
                    <span className="bell-item-title">{label(r.action)}</span>
                    <span className="bell-item-time">{relTime(r.occurred_at)}{r.actor_type ? ` · ${r.actor_type}` : ''}</span>
                  </div>
                </NavLink>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
